import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Group,
  Card,
  Skeleton,
  Button,
  Badge,
  Box,
  Alert,
  ActionIcon,
  Tooltip,
  Progress,
  ThemeIcon,
  Drawer,
  Divider,
  Anchor,
  Loader,
  SegmentedControl,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useFullscreen, useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconPlayerPlay,
  IconArrowLeft,
  IconArrowRight,
  IconLock,
  IconAlertTriangle,
  IconMaximize,
  IconMinimize,
  IconCheck,
  IconHeadphones,
  IconFileText,
  IconExternalLink,
  IconDownload,
  IconLayoutBottombar,
  IconHome,
  IconChevronRight,
  IconMovie,
} from '@tabler/icons-react';
import {
  usePublicCoursesService,
  useCourseService,
  useCourseSessionService,
  useCourseSessionTypeService,
  useCourseSessionStatusService,
  useCoursePrerequisiteService,
} from '@actschurch/ccms-sdk';
import type {
  Course,
  CourseSession,
  CourseModelView,
  CoursePrerequisite,
  ResourceMediaView,
} from '@actschurch/ccms-sdk';
import { PlayerLayout } from '@/layouts/PlayerLayout';
import { useSessionTelemetry } from '@/hooks/useSessionTelemetry';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { RichText } from '@/components/common/RichText';
import { courseHref, matchesSlug } from '@/utils/slug';
import { SignInPromptModal } from '@/components/SignInPromptModal';
import { CurriculumRail } from '@/components/CurriculumRail';
import { SessionCompletionModal } from '@/components/SessionCompletionModal';
import { useSessionResume } from '@/hooks/useSessionResume';
import { usePublicResourceSettings, useRemoteText, useSessionMedia } from '@/hooks/useSessionMedia';
import { HlsVideo } from '@/components/player/HlsVideo';
import { CaptionsTrack } from '@/components/player/CaptionsTrack';
import { ListenPlayer, type PlaybackHandoff } from '@/components/player/ListenPlayer';
import { transcriptSourceFor } from '@/components/player/transcriptSource';
import {
  createPlaybackController,
  useBindMediaElement,
  type PlaybackController,
} from '@/components/player/playbackController';

/**
 * Immersive session player with a collapsible right-side curriculum rail
 * (Up Next / Overview / Notes tabs), fullscreen support, and polished
 * renderers for every media type the backend ships:
 *  - Video — 16:9 surface with custom progress bar, fullscreen.
 *  - Audio — large card with thumbnail + native audio controls.
 *  - PDF / Reading material — embedded viewer with download + "Mark as read".
 *  - Article — rich-text body with explicit "Mark as read" CTA.
 */
export default function SessionPlayer() {
  const params = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useOptionalAuth();
  const { user } = useEnrichedAuth();

  const publicService = usePublicCoursesService();
  const courseService = useCourseService();
  const sessionService = useCourseSessionService();
  const sessionTypeService = useCourseSessionTypeService();
  const sessionStatusService = useCourseSessionStatusService();
  const prereqService = useCoursePrerequisiteService();
  const queryClient = useQueryClient();

  const sessionId = Number(params.sessionId);
  const slug = params.slug;
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Catalog → resolve slug locally; the SDK still drives every follow-up call
  // by numeric courseID.
  const { data: catalog = [], isLoading: loadingCatalog } = useQuery<CourseModelView[]>({
    queryKey: ['public', 'courses', 'view'],
    queryFn: () => publicService.listView(),
  });

  const { data: privateCatalog = [], isLoading: loadingPrivate } = useQuery<Course[]>({
    queryKey: ['courses', 'all', 'authed'],
    queryFn: () => courseService.getAll(),
    enabled: isAuthenticated,
  });

  const course = useMemo<(Course | CourseModelView) | null>(() => {
    if (!slug) return null;
    const fromPublic = catalog.find((c) => matchesSlug(slug, c));
    if (fromPublic) return fromPublic;
    if (isAuthenticated) return privateCatalog.find((c) => matchesSlug(slug, c)) ?? null;
    return null;
  }, [slug, catalog, privateCatalog, isAuthenticated]);

  const loadingCourse = loadingCatalog || (isAuthenticated && loadingPrivate);

  const { data: rawSiblings = [] } = useQuery<CourseSession[]>({
    queryKey: ['course-sessions', course?.courseID, 'for-player', isAuthenticated],
    queryFn: () => {
      if (!course) return Promise.resolve([]);
      return isAuthenticated
        ? sessionService.getByCourse(course.courseID)
        : publicService.getSessionsByCourseId(course.courseID);
    },
    enabled: !!course?.courseID,
  });

  // Defensive draft filter — drop any session whose status is "Draft" so the
  // curriculum drawer never reveals an unpublished session even if a stale
  // backend slips through.
  const { data: sessionStatuses = [] } = useQuery({
    queryKey: ['resources', 'course-session-statuses'],
    queryFn: () => sessionStatusService.getAll(),
  });
  const draftSessionStatusIds = useMemo(
    () => new Set(
      sessionStatuses
        .filter((s) => (s.name ?? '').trim().toLowerCase() === 'draft')
        .map((s) => s.statusID),
    ),
    [sessionStatuses],
  );
  const siblings = useMemo(
    () => rawSiblings.filter((s) => !draftSessionStatusIds.has(s.statusID)),
    [rawSiblings, draftSessionStatusIds],
  );

  const { data: session, isLoading: loadingSession } = useQuery<CourseSession | null>({
    queryKey: ['course-session', sessionId, isAuthenticated],
    queryFn: () => {
      if (!sessionId) return Promise.resolve(null);
      if (isAuthenticated) return sessionService.getById(sessionId);
      return Promise.resolve(siblings.find((s) => s.sessionID === sessionId) ?? null);
    },
    enabled: !!sessionId && (isAuthenticated || siblings.length > 0),
  });

  const { data: types = [] } = useQuery({
    queryKey: ['resources', 'course-session-types'],
    queryFn: () => sessionTypeService.getAll(),
  });
  const sessionType = session ? types.find((t) => t.typeID === session.typeID) : undefined;

  // Gating is per-SESSION, not per-course. A course-visibility check used
  // to gate every session past the first when the viewer was anonymous,
  // which contradicted the model — public courses were meant to play
  // freely. The new rule:
  //   - Sessions with status "Public - Signed In" (formerly "Internal")
  //     require sign-in to play.
  //   - Sessions with any other published status (e.g. "Public") play
  //     for everyone.
  //   - Drafts are already filtered out upstream.
  const signInOnlySessionStatusIds = useMemo(
    () => new Set(
      sessionStatuses
        .filter((s) => {
          const name = (s.name ?? '').trim().toLowerCase();
          return name === 'public - signed in' || name === 'internal';
        })
        .map((s) => s.statusID),
    ),
    [sessionStatuses],
  );
  const isThisSessionGated = !isAuthenticated && session != null && signInOnlySessionStatusIds.has(session.statusID);
  // Kept for the rail to know whether to show the lock icon on each row.
  const isGatedRow = (s: CourseSession) => !isAuthenticated && signInOnlySessionStatusIds.has(s.statusID);
  // Backwards-compat name used by older callsites in this file.
  const isGated = isThisSessionGated;
  const [signInOpen, setSignInOpen] = useState(false);
  // Side rail toggle for desktop. Mobile uses a bottom drawer instead.
  const [railOpened, railHandlers] = useDisclosure(true);
  const [mobileDrawerOpened, mobileDrawerHandlers] = useDisclosure(false);
  const [completionOpen, completionHandlers] = useDisclosure(false);
  // Latch — flips true the first time the player reports completion for THIS
  // session, prevents the modal from re-opening if the user dismisses then
  // scrubs to the end again. Reset on session change.
  const sessionCompletedRef = useRef(false);

  // Side rail is desktop-only; on mobile we surface the same content via a
  // bottom drawer (triggered by the floating "Curriculum" pill).
  useEffect(() => {
    if (isMobile) {
      railHandlers.close();
      mobileDrawerHandlers.close();
    } else {
      railHandlers.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // If the current session itself requires sign-in for an anonymous viewer,
  // surface the prompt modal automatically when the page loads. Public
  // sessions never trigger this.
  useEffect(() => {
    if (isThisSessionGated) setSignInOpen(true);
  }, [isThisSessionGated]);

  // Prerequisite gate. The CourseDetail page already disables the Start
  // button + curriculum-row links when required prereqs are unmet, but a
  // signed-in viewer can still arrive here via a direct URL or a stale
  // bookmark. Fetch the same /unmet-for-me list the course page uses and
  // bounce back to the course detail if anything's outstanding so the
  // viewer never reaches the actual media. Public visitors get a separate
  // sign-in path further up, so this only runs for authenticated viewers.
  //
  // Public prereq edges (anon-readable) are also fetched so we can fall
  // back to a "your account has no progress yet" assumption when the
  // authenticated viewer has never opened any prereq.
  const { data: unmetPrereqs = [], isLoading: loadingUnmet } = useQuery<CoursePrerequisite[]>({
    queryKey: ['course-prerequisites-unmet', course?.courseID, 'player'],
    queryFn: () => (course ? prereqService.getUnmetForMe(course.courseID) : Promise.resolve([])),
    enabled: !!course?.courseID && isAuthenticated,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!course || !isAuthenticated || loadingUnmet) return;
    if (unmetPrereqs.length === 0) return;
    // Redirect back to the course detail page so the viewer can see which
    // prereqs are outstanding and how to get to them. `replace` so the
    // player doesn't sit on the back-stack as an unreachable URL.
    navigate(courseHref(course), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.courseID, isAuthenticated, loadingUnmet, unmetPrereqs.length]);

  const telemetry = useSessionTelemetry(
    session && course
      ? { sessionId: session.sessionID, courseId: course.courseID, ministryId: (course as Partial<CourseModelView>).ministryID ?? undefined }
      : null,
  );

  const currentIndex = siblings.findIndex((s) => s.sessionID === sessionId);
  const prev = currentIndex > 0 ? siblings[currentIndex - 1] : null;
  const next = currentIndex >= 0 && currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : null;

  // Reset the completion latch whenever the user navigates to a different
  // session, so the modal opens once per session.
  useEffect(() => {
    sessionCompletedRef.current = false;
    completionHandlers.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Resume hook — restores playback position when the viewer returns to a
  // session they didn't finish. Cleared on completion so the modal doesn't
  // recommend "resume from 0:30 of 0:30".
  const userId = (user as { userId?: string; id?: string } | null)?.userId ?? (user as { id?: string } | null)?.id ?? null;
  const resume = useSessionResume(session?.sessionID ?? null, userId);

  // Wrapper around the telemetry's onCompleted callback so finishing the
  // media also opens the celebration modal. Uses a ref-backed latch to
  // avoid re-opening if the player fires onCompleted twice (e.g. video
  // end + scrub-to-end).
  const handleCompleted = useCallback(
    (sec: number, pct: number) => {
      telemetry.onCompleted(sec, pct);
      // Stamp this session as completed in the local progress store so the
      // course detail page picks up the green tick. We pass the duration
      // (when known) so anyone re-opening the session sees "Done" instead
      // of being asked to resume from 0:30 of 0:30.
      resume.markComplete(session?.durationInSeconds ?? sec);
      // Refresh the server-computed progress so the curriculum drawer and the
      // "My learning" page reflect this completion without a manual reload.
      queryClient.invalidateQueries({ queryKey: ['course-progress-computed'] });
      queryClient.invalidateQueries({ queryKey: ['my-course-progress-computed'] });
      queryClient.invalidateQueries({ queryKey: ['my-progress-computed'] });
      if (!sessionCompletedRef.current) {
        sessionCompletedRef.current = true;
        completionHandlers.open();
      }
    },
    [telemetry, completionHandlers, resume, session?.durationInSeconds, queryClient],
  );

  // Persist progress as the viewer watches (throttled inside the hook).
  const handleProgress = useCallback(
    (sec: number, pct: number) => {
      telemetry.onProgress(sec, pct);
      resume.save(sec, session?.durationInSeconds ?? 0);
    },
    [telemetry, resume, session?.durationInSeconds],
  );

  // Streaming media (HLS, captions, transcript, listen mode). Progressive
  // enhancement only: while it loads, fails or is switched off, the player
  // behaves exactly as before and plays session.sessionURL.
  const sessionKind = session ? detectMediaKind(sessionType?.name, session.sessionURL ?? null) : null;
  const mediaEligible =
    !!session && !!session.sessionURL && !isThisSessionGated && (sessionKind === 'video' || sessionKind === 'audio');
  const mediaQuery = useSessionMedia(course?.courseID, session?.sessionID, mediaEligible);
  const media = mediaEligible ? mediaQuery.data ?? null : null;
  const { data: publicSettings } = usePublicResourceSettings();
  const [playback] = useState<PlaybackController>(createPlaybackController);
  const transcriptsOff = publicSettings?.enableTranscripts === false;
  const transcriptSource = useMemo(() => transcriptSourceFor(media, transcriptsOff), [media, transcriptsOff]);

  const courseUrl = course ? courseHref(course) : '/courses';

  // Filename stem for the notes export — humans see this in their downloads.
  const exportNameBase = course && session
    ? `${(course.name ?? 'course').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${(session.name ?? `session-${session.sessionID}`).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-notes`
    : 'session-notes';

  const transcriptNameBase = exportNameBase.replace(/-notes$/, '');

  const ministryName = (course as Partial<CourseModelView>)?.ministryName ?? null;

  if (loadingCourse || loadingSession) {
    return (
      <PlayerLayout>
        <Box p="lg">
          <Skeleton height={480} radius="md" />
        </Box>
      </PlayerLayout>
    );
  }

  if (!session || !course) {
    return (
      <PlayerLayout>
        <Box p="lg">
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
            This session could not be loaded.
          </Alert>
        </Box>
      </PlayerLayout>
    );
  }

  const totalSessions = siblings.length;
  const courseProgress = totalSessions > 0 ? ((currentIndex + 1) / totalSessions) * 100 : 0;

  return (
    <>
      <PlayerLayout
        title={course.name ?? 'Course'}
        backHref={courseUrl}
        hideRail={isMobile}
        headerCenter={
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0, maxWidth: '100%' }}>
            <ActionIcon component={Link} to="/" variant="subtle" size="sm" aria-label="Home">
              <IconHome size={14} />
            </ActionIcon>
            <IconChevronRight size={12} color="var(--mantine-color-dimmed)" />
            {ministryName && (
              <>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {ministryName}
                </Text>
                <IconChevronRight size={12} color="var(--mantine-color-dimmed)" />
              </>
            )}
            <Anchor component={Link} to={courseUrl} size="xs" c="brand" style={{ whiteSpace: 'nowrap' }}>
              {course.name ?? 'Course'}
            </Anchor>
            <IconChevronRight size={12} color="var(--mantine-color-dimmed)" />
            <Text size="xs" fw={600} style={{ whiteSpace: 'nowrap' }}>
              Session {currentIndex + 1}
              {totalSessions > 0 ? ` of ${totalSessions}` : ''}
            </Text>
          </Group>
        }
        rail={
          <CurriculumRail
            course={course}
            sessions={siblings}
            currentSessionId={sessionId}
            isSessionLocked={isGatedRow}
            userId={userId}
            notesExportName={exportNameBase}
            transcript={transcriptSource ? { ...transcriptSource, controller: playback, fileNameBase: transcriptNameBase } : null}
          />
        }
        railOpen={railOpened}
        onToggleRail={railHandlers.toggle}
      >
        {/*
          Viewport-locked column: header (56px) + this column = 100vh, so the
          play controls + nav buttons are always visible without scrolling.
          Media surface gets `flex: 1, minHeight: 0` so it grows to fill
          remaining space; for video/audio the renderer uses object-fit:
          contain so nothing stretches.
        */}
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 56px)',
            padding: 'var(--mantine-spacing-md)',
            gap: 'var(--mantine-spacing-sm)',
            minHeight: 0,
          }}
        >
          {/* Top meta strip — desktop puts everything on one row, mobile
              stacks the title above the badges + progress so the title gets
              full width and isn't truncated to "Sessi…". */}
          <Stack gap={6} style={{ flex: '0 0 auto' }}>
            <Group justify="space-between" wrap="nowrap" align="center" gap="xs">
              <Title
                order={5}
                lh={1.2}
                style={{
                  minWidth: 0,
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {session.name}
              </Title>
              {totalSessions > 0 && (
                <Text size="xs" c="dimmed" fw={600} style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}>
                  {currentIndex + 1} / {totalSessions}
                </Text>
              )}
            </Group>
            <Group justify="space-between" wrap="nowrap" align="center" gap="xs">
              <Group gap={6} wrap="nowrap" style={{ flex: '0 0 auto' }}>
                {sessionType && (
                  <Badge
                    variant="light"
                    color="brand"
                    size="sm"
                    leftSection={<MediaIcon kind={detectMediaKind(sessionType.name, session.sessionURL ?? null)} />}
                  >
                    {sessionType.name}
                  </Badge>
                )}
                {isGated && (
                  <Tooltip label="Sign-in required">
                    <Badge variant="light" color="gray" size="sm" leftSection={<IconLock size={10} />} aria-label="Sign-in required">
                      <Box visibleFrom="sm" component="span">Sign-in required</Box>
                      <Box hiddenFrom="sm" component="span">
                        &nbsp;
                      </Box>
                    </Badge>
                  </Tooltip>
                )}
              </Group>
              {totalSessions > 0 && (
                <Progress value={courseProgress} size="xs" radius="xl" color="brand" style={{ flex: 1, minWidth: 80 }} />
              )}
            </Group>
          </Stack>

          {/* Media gets all remaining height. minHeight:0 is critical so the
              flex child can shrink instead of overflowing. */}
          <Box style={{ flex: '1 1 auto', minHeight: 0, display: 'flex' }}>
            <MediaSurface
              session={session}
              courseId={course.courseID}
              typeName={sessionType?.name}
              onProgress={handleProgress}
              onCompleted={handleCompleted}
              disabled={isThisSessionGated}
              resumeAt={resume.resumeAt}
              media={media}
              mediaLoading={mediaEligible && mediaQuery.isLoading}
              controller={playback}
            />
          </Box>

          {/* Bottom nav bar — always visible. The middle slot is a single
              "Actions" menu (replaces the share/bookmark/curriculum icons
              that used to overlap the video controls). On mobile it also
              hosts the curriculum trigger that previously floated. */}
          <Group justify="space-between" wrap="nowrap" style={{ flex: '0 0 auto' }}>
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              disabled={!prev}
              onClick={() => prev && navigate(`${courseUrl}/play/${prev.sessionID}`)}
              size="sm"
            >
              Previous
            </Button>

            {/* Single button that opens the drawer on mobile or toggles the
                side rail on desktop. Every other action (share, bookmark,
                course overview, home) lives inside the drawer's Overview tab
                so the footer stays clean and the video controls aren't
                obstructed. */}
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconLayoutBottombar size={16} />}
              onClick={isMobile ? mobileDrawerHandlers.open : railHandlers.toggle}
            >
              {isMobile ? 'Curriculum' : railOpened ? 'Hide curriculum' : 'Curriculum'}
              {totalSessions > 0 && (
                <Text component="span" size="xs" c="dimmed" ml={6}>
                  {currentIndex + 1} / {totalSessions}
                </Text>
              )}
            </Button>

            <Button
              variant="filled"
              rightSection={<IconArrowRight size={16} />}
              disabled={!next}
              onClick={() => next && navigate(`${courseUrl}/play/${next.sessionID}`)}
              size="sm"
            >
              Next
            </Button>
          </Group>
        </Box>
      </PlayerLayout>

      {/* Mobile (≤ 768px) replaces the side rail with a bottom drawer. The
          trigger lives inside the footer "Actions" menu — no floating pill,
          so the native video/audio controls stay unobstructed. */}
      {isMobile && (
        <Drawer
          opened={mobileDrawerOpened}
          onClose={mobileDrawerHandlers.close}
          position="bottom"
          size="80%"
          padding={0}
          withCloseButton
          title={
            <Group gap="xs">
              <IconLayoutBottombar size={16} />
              <Text fw={600}>{course.name ?? 'Course'}</Text>
            </Group>
          }
          styles={{
            body: { padding: 0, height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column' },
          }}
        >
          <Divider />
          <Box style={{ flex: 1, minHeight: 0 }}>
            <CurriculumRail
              course={course}
              sessions={siblings}
              currentSessionId={sessionId}
              isSessionLocked={isGatedRow}
              userId={userId}
              notesExportName={exportNameBase}
              transcript={transcriptSource ? { ...transcriptSource, controller: playback, fileNameBase: transcriptNameBase } : null}
            />
          </Box>
        </Drawer>
      )}

      <SignInPromptModal
        opened={signInOpen}
        onClose={() => setSignInOpen(false)}
        title="This session is for signed-in viewers"
        description="Sign in to unlock the rest of the course. The first session is free to preview."
      />

      <SessionCompletionModal
        opened={completionOpen}
        onClose={completionHandlers.close}
        sessionName={session.name ?? `Session ${currentIndex + 1}`}
        courseName={course.name ?? 'this course'}
        nextSessionName={next?.name ?? null}
        position={totalSessions > 0 ? { current: currentIndex + 1, total: totalSessions } : undefined}
        onStartNext={() => {
          if (next) navigate(`${courseUrl}/play/${next.sessionID}`);
        }}
        onBrowseCourses={() => navigate('/courses')}
      />
    </>
  );
}

/**
 * Wrapper around the type-specific renderer that owns the fullscreen surface.
 * Keeps the toggle in one place so video/audio/pdf/reading all benefit.
 */
interface MediaSurfaceProps {
  session: CourseSession;
  courseId: number;
  typeName?: string | null;
  onProgress: (sec: number, pct: number) => void;
  onCompleted: (sec: number, pct: number) => void;
  disabled: boolean;
  /** Playback position (seconds) to seek to once metadata is ready. 0 = start fresh. */
  resumeAt?: number;
  /** Streaming media for the session; null = play the original file as before. */
  media?: ResourceMediaView | null;
  /** True while the first media lookup is in flight. */
  mediaLoading?: boolean;
  /** Links the active media element to the transcript panel. */
  controller?: PlaybackController | null;
}

/** Longest the video waits for the media lookup before playing the original file. */
const MEDIA_WAIT_MS = 3_000;

function MediaSurface({
  session,
  courseId,
  typeName,
  onProgress,
  onCompleted,
  disabled,
  resumeAt,
  media,
  mediaLoading = false,
  controller,
}: MediaSurfaceProps) {
  const { ref, toggle, fullscreen } = useFullscreen<HTMLDivElement>();
  const kind = detectMediaKind(typeName, session.sessionURL ?? null);
  // Reading material has its own scrollable interior so it can flex; the
  // others (video/audio/pdf) want to render at the surface size.
  const scrollableInterior = kind === 'reading';

  // Hold the video briefly for the media lookup so a viewer never starts the
  // original file only to have it swapped for the stream a moment later.
  const [waitedOutFor, setWaitedOutFor] = useState<number | null>(null);
  useEffect(() => {
    if (!mediaLoading) return;
    const id = session.sessionID;
    const t = window.setTimeout(() => setWaitedOutFor(id), MEDIA_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [mediaLoading, session.sessionID]);
  const holdForMedia = kind === 'video' && mediaLoading && waitedOutFor !== session.sessionID;

  // Watch / Listen (videos with an audio rendition only). The handoff carries
  // position + play state across the swap and belongs to one session only.
  const [preferListen, setPreferListen] = useListenPreference(session.sessionID);
  const [handoffState, setHandoffState] = useState<{ sessionId: number; value: PlaybackHandoff | null } | null>(null);
  const handoff = handoffState?.sessionId === session.sessionID ? handoffState.value : null;
  const setHandoff = (value: PlaybackHandoff | null) => setHandoffState({ sessionId: session.sessionID, value });
  const canListen = !disabled && kind === 'video' && !!media?.audioUrl && !holdForMedia;
  const mode: 'watch' | 'listen' = canListen && preferListen ? 'listen' : 'watch';

  const switchMode = (next: 'watch' | 'listen') => {
    if (next === mode) return;
    const el = controller?.getElement();
    setHandoff(el ? { time: el.currentTime || 0, play: !el.paused && !el.ended } : null);
    setPreferListen(next === 'listen');
  };

  // The MP3 failed: go back to the video at the same spot.
  const onListenFailed = () => {
    const el = controller?.getElement();
    setHandoff(el ? { time: el.currentTime || 0, play: false } : null);
    setPreferListen(false);
  };

  const publicService = usePublicCoursesService();
  const [downloadingAudio, setDownloadingAudio] = useState(false);
  const canDownloadAudio = !disabled && !!media?.audioDownloadAvailable;
  const downloadAudio = async () => {
    setDownloadingAudio(true);
    try {
      const res = await publicService.getSessionAudioDownload(courseId, session.sessionID);
      if (res?.url) {
        window.location.assign(res.url);
      } else {
        throw new Error('No download link');
      }
    } catch {
      notifications.show({ color: 'red', message: 'The audio download is not available right now.' });
    } finally {
      setDownloadingAudio(false);
    }
  };

  return (
    <Card
      ref={ref}
      withBorder
      radius="md"
      padding={0}
      shadow="md"
      style={{
        overflow: 'hidden',
        background: kind === 'video' ? '#000' : 'var(--mantine-color-body)',
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: scrollableInterior ? 'auto' : 'hidden',
        }}
      >
        <MediaRenderer
          session={session}
          kind={kind}
          onProgress={onProgress}
          onCompleted={onCompleted}
          disabled={disabled}
          resumeAt={resumeAt}
          media={media}
          holdForMedia={holdForMedia}
          mode={mode}
          handoff={handoff}
          controller={controller}
          onListenFailed={onListenFailed}
        />
      </Box>

      {(canListen || canDownloadAudio) && (
        <Group
          gap={6}
          wrap="nowrap"
          style={{ position: 'absolute', top: 12, left: 12, zIndex: 5, maxWidth: 'calc(100% - 72px)' }}
        >
          {canListen && (
            <SegmentedControl
              size="xs"
              radius="xl"
              value={mode}
              onChange={(v) => switchMode(v === 'listen' ? 'listen' : 'watch')}
              aria-label="Watch or listen"
              data={[
                {
                  value: 'watch',
                  label: (
                    <Group gap={4} wrap="nowrap" justify="center">
                      <IconMovie size={12} />
                      <span>Watch</span>
                    </Group>
                  ),
                },
                {
                  value: 'listen',
                  label: (
                    <Group gap={4} wrap="nowrap" justify="center">
                      <IconHeadphones size={12} />
                      <span>Listen</span>
                    </Group>
                  ),
                },
              ]}
              style={{ opacity: 0.92, boxShadow: '0 2px 10px rgba(0,0,0,0.25)' }}
            />
          )}
          {canDownloadAudio && (
            <Button
              size="compact-xs"
              radius="xl"
              variant="white"
              color="dark"
              leftSection={<IconDownload size={12} />}
              onClick={downloadAudio}
              loading={downloadingAudio}
              style={{ opacity: 0.92, boxShadow: '0 2px 10px rgba(0,0,0,0.25)' }}
            >
              Download audio
            </Button>
          )}
        </Group>
      )}

      {!disabled && session.sessionURL && (
        <Tooltip label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
          <ActionIcon
            variant="filled"
            color="dark"
            size="lg"
            radius="xl"
            aria-label="Toggle fullscreen"
            onClick={toggle}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              opacity: 0.7,
              backdropFilter: 'blur(4px)',
              zIndex: 5,
            }}
          >
            {fullscreen ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
          </ActionIcon>
        </Tooltip>
      )}
    </Card>
  );
}

interface MediaRendererProps {
  session: CourseSession;
  kind: MediaKind;
  onProgress: (sec: number, pct: number) => void;
  onCompleted: (sec: number, pct: number) => void;
  disabled: boolean;
  resumeAt?: number;
  media?: ResourceMediaView | null;
  /** Show a short loading state while the media lookup finishes (video only). */
  holdForMedia?: boolean;
  mode?: 'watch' | 'listen';
  handoff?: PlaybackHandoff | null;
  controller?: PlaybackController | null;
  onListenFailed?: () => void;
}

function MediaRenderer({
  session,
  kind,
  onProgress,
  onCompleted,
  disabled,
  resumeAt,
  media,
  holdForMedia = false,
  mode = 'watch',
  handoff,
  controller,
  onListenFailed,
}: MediaRendererProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const bindAudio = useBindMediaElement(controller);
  const setAudioRef = useCallback(
    (node: HTMLAudioElement | null) => {
      audioRef.current = node;
      bindAudio(node);
    },
    [bindAudio],
  );

  if (disabled) {
    return (
      <Stack p="xl" align="center" gap="sm" style={{ minHeight: 280, justifyContent: 'center' }}>
        <ThemeIcon size={56} radius="xl" variant="light" color="gray">
          <IconLock size={28} />
        </ThemeIcon>
        <Text c="dimmed" fw={500}>Sign in to keep watching.</Text>
      </Stack>
    );
  }

  if (!session.sessionURL && (kind === 'video' || kind === 'audio' || kind === 'pdf')) {
    return (
      <Stack p="xl" align="center" gap="sm" style={{ minHeight: 280, justifyContent: 'center' }}>
        <ThemeIcon size={56} radius="xl" variant="light" color="brand">
          <IconPlayerPlay size={28} />
        </ThemeIcon>
        <Text c="dimmed" fw={500}>Session media will be available soon.</Text>
      </Stack>
    );
  }

  const handleTime = (e: { currentTarget: HTMLMediaElement }) => {
    const el = e.currentTarget;
    const pct = el.duration > 0 ? Math.min(100, (el.currentTime / el.duration) * 100) : 0;
    onProgress(Math.floor(el.currentTime), Math.round(pct * 100) / 100);
  };

  const handleEnd = (e: { currentTarget: HTMLMediaElement }) => {
    const el = e.currentTarget;
    const pct = el.duration > 0 ? 100 : 0;
    onCompleted(Math.floor(el.duration ?? session.durationInSeconds ?? 0), pct);
  };

  switch (kind) {
    case 'video':
      if (holdForMedia) {
        return (
          <Box
            style={{
              width: '100%',
              height: '100%',
              background: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loader color="gray" size="md" />
          </Box>
        );
      }
      if (mode === 'listen' && media?.audioUrl) {
        return (
          <ListenPlayer
            key={`listen-${session.sessionID}`}
            audioUrl={media.audioUrl}
            artworkUrl={session.thumbnail ?? media.posterUrl ?? null}
            title={session.name}
            color={session.color}
            handoff={handoff}
            resumeAt={resumeAt ?? 0}
            controller={controller}
            onProgress={onProgress}
            onCompleted={onCompleted}
            onFailed={() => onListenFailed?.()}
          />
        );
      }
      // Stateful video — adds a centered play overlay so first-time viewers
      // see clearly that they need to start playback, and seeks to any
      // resume position once metadata is ready.
      return (
        <VideoPlayer
          key={`watch-${session.sessionID}`}
          src={session.sessionURL ?? undefined}
          poster={session.thumbnail ?? media?.posterUrl ?? undefined}
          resumeAt={resumeAt ?? 0}
          onProgress={(sec, pct) => onProgress(sec, pct)}
          onCompleted={(sec, pct) => onCompleted(sec, pct)}
          hlsUrl={media?.hlsUrl ?? null}
          captionsVttUrl={media?.captionsVttUrl ?? null}
          captionsLanguage={media?.transcriptLanguage ?? null}
          handoff={handoff}
          controller={controller}
        />
      );

    case 'audio':
      return (
        <Stack
          p="xl"
          gap="lg"
          align="center"
          style={{
            background: session.color
              ? `linear-gradient(135deg, ${session.color}33, var(--mantine-color-brand-light))`
              : 'linear-gradient(135deg, var(--mantine-color-brand-light), var(--mantine-color-default-hover))',
            flex: 1,
            minHeight: 0,
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {session.thumbnail ? (
            <img
              src={session.thumbnail}
              alt=""
              style={{
                maxWidth: 'min(28vh, 220px)',
                maxHeight: 'min(28vh, 220px)',
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                borderRadius: 16,
                boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
              }}
            />
          ) : (
            <ThemeIcon size={120} radius={120} variant="filled" color="brand">
              <IconHeadphones size={56} />
            </ThemeIcon>
          )}
          <audio
            ref={setAudioRef}
            src={session.sessionURL ?? undefined}
            controls
            preload="metadata"
            onTimeUpdate={handleTime}
            onEnded={handleEnd}
            style={{ width: '100%', maxWidth: 640 }}
          />
        </Stack>
      );

    case 'pdf':
      return (
        <>
          <Group
            justify="space-between"
            px="md"
            py="xs"
            style={{
              borderBottom: '1px solid var(--mantine-color-default-border)',
              flex: '0 0 auto',
            }}
          >
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="red">
                <IconFileText size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500} lineClamp={1}>
                {session.name ?? 'PDF document'}
              </Text>
            </Group>
            <Group gap="xs">
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconDownload size={14} />}
                component="a"
                href={session.sessionURL ?? '#'}
                download
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </Button>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconCheck size={14} />}
                onClick={() => onCompleted(session.durationInSeconds ?? 0, 100)}
              >
                Mark as read
              </Button>
            </Group>
          </Group>
          <iframe
            src={session.sessionURL ?? undefined}
            title={session.name ?? 'PDF'}
            style={{
              border: 0,
              width: '100%',
              flex: 1,
              minHeight: 0,
              display: 'block',
            }}
            onLoad={() => onProgress(session.durationInSeconds ?? 0, 100)}
          />
        </>
      );

    case 'reading':
    default:
      return (
        <>
          <Group
            justify="space-between"
            px="md"
            py="xs"
            style={{
              borderBottom: '1px solid var(--mantine-color-default-border)',
              flex: '0 0 auto',
              position: 'sticky',
              top: 0,
              background: 'var(--mantine-color-body)',
              zIndex: 1,
            }}
          >
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconFileText size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                Reading material
              </Text>
            </Group>
            <Group gap="xs">
              {session.sessionURL && (
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconExternalLink size={14} />}
                  component="a"
                  href={session.sessionURL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open original
                </Button>
              )}
              <Button
                size="xs"
                variant="light"
                leftSection={<IconCheck size={14} />}
                onClick={() => onCompleted(session.durationInSeconds ?? 0, 100)}
              >
                Mark as read
              </Button>
            </Group>
          </Group>
          <Box p={{ base: 'md', md: 'xl' }} style={{ maxWidth: 800, margin: '0 auto', width: '100%' }}>
            <RichText
              html={session.description}
              fallback="Reading material will be available here."
            />
          </Box>
        </>
      );
  }
}

type MediaKind = 'video' | 'audio' | 'pdf' | 'reading';

function detectMediaKind(typeName?: string | null, url?: string | null): MediaKind {
  const t = (typeName ?? '').toLowerCase();
  if (t.includes('video')) return 'video';
  if (t.includes('audio')) return 'audio';
  if (t.includes('pdf')) return 'pdf';
  if (t.includes('read') || t.includes('article')) return 'reading';
  const lower = (url ?? '').toLowerCase();
  if (/\.(mp4|webm|ogg|mov)(\?|$)/.test(lower)) return 'video';
  if (/\.(mp3|wav|m4a|aac)(\?|$)/.test(lower)) return 'audio';
  if (/\.pdf(\?|$)/.test(lower)) return 'pdf';
  return 'reading';
}

function MediaIcon({ kind }: { kind: MediaKind }) {
  if (kind === 'video') return <IconPlayerPlay size={10} />;
  if (kind === 'audio') return <IconHeadphones size={10} />;
  if (kind === 'pdf') return <IconFileText size={10} />;
  return <IconFileText size={10} />;
}

/**
 * Video element with a giant centered play overlay and resume-from-position.
 *
 *  - Overlay sits above the native controls until the user hits play, so the
 *    "click to start" affordance is unmissable. It re-appears on pause/end.
 *  - On <c>loadedmetadata</c>, if <c>resumeAt</c> is positive and not at the
 *    very end, the element seeks to that timestamp so returning viewers
 *    pick up where they left off.
 *  - Native controls stay on so we don't reinvent volume/scrub/fullscreen.
 *  - Streams HLS through HlsVideo when an `hlsUrl` is supplied, falling back
 *    to `src` (the original file) on any failure. Captions come from a
 *    same-origin blob URL so the element needs no crossOrigin.
 *  - `handoff` continues from Listen mode at the same position and state.
 */
function VideoPlayer({
  src,
  poster,
  resumeAt,
  onProgress,
  onCompleted,
  hlsUrl = null,
  captionsVttUrl = null,
  captionsLanguage = null,
  handoff = null,
  controller,
}: {
  src?: string;
  poster?: string;
  resumeAt: number;
  onProgress: (sec: number, pct: number) => void;
  onCompleted: (sec: number, pct: number) => void;
  hlsUrl?: string | null;
  captionsVttUrl?: string | null;
  captionsLanguage?: string | null;
  handoff?: PlaybackHandoff | null;
  controller?: PlaybackController | null;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [paused, setPaused] = useState(true);
  const [hasStarted, setHasStarted] = useState(!!handoff);
  const seekedRef = useRef(false);
  const startedRef = useRef(!!handoff?.play);

  const bind = useBindMediaElement(controller);
  const setVideoRef = useCallback(
    (node: HTMLVideoElement | null) => {
      ref.current = node;
      bind(node);
    },
    [bind],
  );

  // Adopt a stream URL only before playback starts, so a media status poll
  // never swaps the source under a viewer mid-session.
  const [streamUrl, setStreamUrl] = useState<string | null>(hlsUrl);
  useEffect(() => {
    if (!startedRef.current) setStreamUrl(hlsUrl);
  }, [hlsUrl]);

  const captions = useRemoteText(captionsVttUrl);

  // Re-seek whenever the source changes. hls.js leaves the element's src
  // unset, so this keys on the stream + file pair rather than on src.
  const sourceKey = `${streamUrl ?? ''}|${src ?? ''}`;
  useEffect(() => {
    seekedRef.current = false;
  }, [sourceKey]);

  const onLoadedMetadata = () => {
    const el = ref.current;
    if (!el || seekedRef.current) return;
    seekedRef.current = true;
    if (handoff) {
      if (handoff.time > 0.5 && (!(el.duration > 0) || handoff.time < el.duration - 0.5)) {
        try { el.currentTime = handoff.time; } catch { /* ignore seek errors */ }
      }
      if (handoff.play) el.play().catch(() => setPaused(true));
      return;
    }
    if (resumeAt > 1 && el.duration > 0 && resumeAt < el.duration - 2) {
      try { el.currentTime = resumeAt; } catch { /* ignore seek errors */ }
    }
  };

  const handleTime = () => {
    const el = ref.current;
    if (!el) return;
    const pct = el.duration > 0 ? Math.min(100, (el.currentTime / el.duration) * 100) : 0;
    onProgress(Math.floor(el.currentTime), Math.round(pct * 100) / 100);
  };

  const handleEnd = () => {
    const el = ref.current;
    if (!el) return;
    const pct = el.duration > 0 ? 100 : 0;
    onCompleted(Math.floor(el.duration ?? 0), pct);
  };

  const triggerPlay = () => {
    const el = ref.current;
    if (!el) return;
    setHasStarted(true);
    el.play().catch(() => {
      // Autoplay blocked or user-gesture missing — surface the overlay again.
      setPaused(true);
    });
  };

  const showOverlay = paused && !!src;
  const isResume = hasStarted && resumeAt > 1 ? false : resumeAt > 1;

  return (
    <Box
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: '#000',
      }}
    >
      <HlsVideo
        ref={setVideoRef}
        hlsUrl={streamUrl}
        fallbackSrc={src}
        poster={poster}
        controls
        // Coming back from Listen mode: load straight away so the handoff
        // position can be applied. Otherwise only metadata, as before.
        preload={handoff ? 'auto' : 'metadata'}
        playsInline
        onLoadedMetadata={onLoadedMetadata}
        onPlay={() => { startedRef.current = true; setPaused(false); setHasStarted(true); }}
        onPause={() => setPaused(true)}
        onEnded={() => { setPaused(true); handleEnd(); }}
        onTimeUpdate={handleTime}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          background: '#000',
          display: 'block',
        }}
      >
        {captions.data ? <CaptionsTrack vttText={captions.data} language={captionsLanguage} /> : null}
      </HlsVideo>

      {showOverlay && (
        <button
          type="button"
          onClick={triggerPlay}
          aria-label={isResume ? 'Resume video' : 'Play video'}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            background: hasStarted ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.55)',
            border: 0,
            cursor: 'pointer',
            color: '#fff',
            transition: 'background 200ms ease',
            // Sit above the native control bar (which lives at z-index 0 on
            // most engines) so the overlay click reaches us, not scrub bar.
            zIndex: 2,
            pointerEvents: 'auto',
          }}
        >
          <span
            style={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              color: 'var(--mantine-color-brand-7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 36px rgba(0,0,0,0.45)',
              transition: 'transform 120ms ease',
            }}
          >
            <IconPlayerPlay size={44} fill="currentColor" />
          </span>
          <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: 0.2, textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>
            {isResume ? `Resume from ${formatClock(resumeAt)}` : hasStarted ? 'Tap to resume' : 'Tap to play'}
          </span>
        </button>
      )}
    </Box>
  );
}

function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const LISTEN_PREF_PREFIX = 'rc:session-listen:';

/** Per-session Watch / Listen choice, remembered in localStorage when available. */
function useListenPreference(sessionId: number): [boolean, (listen: boolean) => void] {
  const read = (id: number) => {
    try {
      return window.localStorage.getItem(`${LISTEN_PREF_PREFIX}${id}`) === 'listen';
    } catch {
      return false;
    }
  };
  const [state, setState] = useState<{ id: number; listen: boolean }>(() => ({ id: sessionId, listen: read(sessionId) }));
  const current = state.id === sessionId ? state.listen : read(sessionId);
  useEffect(() => {
    if (state.id !== sessionId) setState({ id: sessionId, listen: read(sessionId) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  const set = useCallback(
    (listen: boolean) => {
      setState({ id: sessionId, listen });
      try {
        if (listen) window.localStorage.setItem(`${LISTEN_PREF_PREFIX}${sessionId}`, 'listen');
        else window.localStorage.removeItem(`${LISTEN_PREF_PREFIX}${sessionId}`);
      } catch {
        /* storage unavailable: the choice lasts for this visit only */
      }
    },
    [sessionId],
  );
  return [current, set];
}
